import { Component, Input, OnChanges, OnInit, QueryList, SimpleChanges, ViewChildren } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

// models
import { DashboardResponse } from 'src/app/pages/dashboard/models/Dashboard.types';
import { ChartType } from './chart-type.model';
import { DecimalPipe } from '@angular/common';
import { ChartComponent } from 'ng-apexcharts';

@Component({
    selector: 'app-stackedBar-chart',
    templateUrl: './stackedBar-chart.component.html',
    styleUrls: ['./stackedBar-chart.component.scss'],
    standalone: false
})
export class StackedBarComponent implements OnInit {

  //  ADD: Aggregation options
  aggregationOptions = [
    { label: 'Daily', value: 'daily' },
    { label: 'Weekly', value: 'weekly' },
    { label: 'Monthly', value: 'monthly' }
  ];

  //  ADD: Quarter filters
  quarterFilters = [
    { label: 'Q1', value: 'Q1', months: [0, 1, 2] }, // Jan, Feb, Mar
    { label: 'Q2', value: 'Q2', months: [3, 4, 5] }, // Apr, May, Jun
    { label: 'Q3', value: 'Q3', months: [6, 7, 8] }, // Jul, Aug, Sep
    { label: 'Q4', value: 'Q4', months: [9, 10, 11] }, // Oct, Nov, Dec
    { label: 'ALL', value: 'ALL', months: [] } // Show all data
  ];

  //  ADD: Filter and aggregation state
  selectedQuarter: string = 'ALL';
  selectedAggregation: string = 'daily';
  originalChartData: any[] = []; // Store original data


  // Add ViewChild to reference the chart
  @ViewChildren('chart') charts: QueryList<ChartComponent>;

  @Input() stackedBarData: DashboardResponse[];
  @Input() chartWidth: string;
  @Input() showAsInteger: boolean = false;

  cashPositioning: any[] = [];
  // Add available chart types
  availableChartTypes = [
    {
      value: 'bar',
      label: 'Bar Chart',
      icon: 'ri-bar-chart-2-line'
    },
    {
      value: 'area',
      label: 'Area Chart',
      icon: 'ri-line-chart-line'
    }
  ];



  cashPositioningNew: ChartType[] = [];

  cashPositioningPerformance!: any;
  constructor(public _translate: TranslateService, private _decimalPipe: DecimalPipe) {

  }

  ngOnInit(): void {
    this.cashPositioningChart()
  }



  private cashPositioningChart() {
    // Clear the existing data
    this.cashPositioningNew = [];
    this.originalChartData = [];

    this.stackedBarData.forEach((chart, index) => {
      let Unit: string = '';

      if (chart.dataSeries) {
        Unit = chart.dataSeries[0].unitEn;

        // Transform multiple series into single time-series
        const cashBalanceData = chart.labelsEn.map((label: string, dateIndex: number) => {
          for (let seriesIndex = 0; seriesIndex < chart.dataSeries.length; seriesIndex++) {
            const value = chart.dataSeries[seriesIndex].data[dateIndex];
            if (value > 0) {
              return value;
            }
          }
          return 0;
        });

        //  NEW: Store original data for filtering
        this.originalChartData[index] = {
          labels: [...chart.labelsEn],
          data: [...cashBalanceData],
          unit: Unit
        };

        //  NEW: Apply smart data processing
        const processedData = this.smartDataProcessing(
          chart.labelsEn,
          cashBalanceData,
          this.selectedQuarter
        );

        //  NEW: Create chart with processed data
        this.createChartConfig(index, processedData, Unit);
      }
    })
  }


  //  ADD: Smart data processing method
  private smartDataProcessing(labels: string[], data: number[], quarter: string) {
    // Step 1: Apply quarter filter
    let filteredData = this.filterDataByQuarter(labels, data, quarter);

    // Step 2: Auto-determine optimal aggregation
    const dataPointCount = filteredData.labels.length;
    let optimalAggregation: string;

    if (dataPointCount <= 31) {
      optimalAggregation = 'daily';
    } else if (dataPointCount <= 120) {
      optimalAggregation = 'weekly';
    } else {
      optimalAggregation = 'monthly';
    }

    // Step 3: Apply aggregation
    this.selectedAggregation = optimalAggregation;
    return this.aggregateData(filteredData.labels, filteredData.data, optimalAggregation);
  }

  //  ADD: Quarter filtering method
  private filterDataByQuarter(labels: string[], data: number[], quarter: string) {
    if (quarter === 'ALL') {
      return { labels, data };
    }

    const selectedQuarter = this.quarterFilters.find(q => q.value === quarter);
    if (!selectedQuarter) {
      return { labels, data };
    }

    const filteredLabels: string[] = [];
    const filteredData: number[] = [];

    labels.forEach((label, index) => {
      const date = new Date(label);
      const month = date.getMonth();

      if (selectedQuarter.months.includes(month)) {
        filteredLabels.push(label);
        filteredData.push(data[index]);
      }
    });

    return {
      labels: filteredLabels,
      data: filteredData
    };
  }

  //  ADD: Data aggregation method
  private aggregateData(labels: string[], data: number[], aggregationType: string) {
    if (aggregationType === 'daily') {
      return { labels, data };
    }

    if (aggregationType === 'weekly') {
      return this.aggregateByWeek(labels, data);
    } else if (aggregationType === 'monthly') {
      return this.aggregateByMonth(labels, data);
    }

    return { labels, data };
  }

  //  ADD: Weekly aggregation
  private aggregateByWeek(labels: string[], data: number[]) {
    const weeklyData: { [key: string]: { sum: number, count: number } } = {};

    labels.forEach((label, index) => {
      const date = new Date(label);
      const weekStart = this.getWeekStart(date);
      const weekKey = weekStart.toISOString().split('T')[0];

      if (!weeklyData[weekKey]) {
        weeklyData[weekKey] = { sum: 0, count: 0 };
      }

      weeklyData[weekKey].sum += data[index];
      weeklyData[weekKey].count += 1;
    });

    const aggregatedLabels: string[] = [];
    const aggregatedData: number[] = [];

    Object.keys(weeklyData).sort().forEach(weekKey => {
      const weekData = weeklyData[weekKey];
      const weekStart = new Date(weekKey);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);

      // Label format: "Jan 1-7"
      const weekLabel = `${weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}-${weekEnd.getDate()}`;

      aggregatedLabels.push(weekLabel);
      aggregatedData.push(weekData.sum / weekData.count); // Average
    });

    return { labels: aggregatedLabels, data: aggregatedData };
  }

  //  ADD: Monthly aggregation
  private aggregateByMonth(labels: string[], data: number[]) {
    const monthlyData: { [key: string]: { sum: number, count: number } } = {};

    labels.forEach((label, index) => {
      const date = new Date(label);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = { sum: 0, count: 0 };
      }

      monthlyData[monthKey].sum += data[index];
      monthlyData[monthKey].count += 1;
    });

    const aggregatedLabels: string[] = [];
    const aggregatedData: number[] = [];

    Object.keys(monthlyData).sort().forEach(monthKey => {
      const [year, month] = monthKey.split('-');
      const date = new Date(parseInt(year), parseInt(month) - 1, 1);
      const monthLabel = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });

      aggregatedLabels.push(monthLabel);
      aggregatedData.push(monthlyData[monthKey].sum / monthlyData[monthKey].count); // Average
    });

    return { labels: aggregatedLabels, data: aggregatedData };
  }

  //  ADD: Get week start helper
  private getWeekStart(date: Date): Date {
    const day = date.getDay();
    const diff = date.getDate() - day + (day === 0 ? -6 : 1); // Monday start
    return new Date(date.setDate(diff));
  }

  //  ADD: Create chart configuration
  private createChartConfig(index: number, processedData: { labels: string[], data: number[] }, Unit: string) {
    const series = [{
      name: 'Cash Balance',
      data: processedData.data
    }];

    const seriesColors = ["#749819"];
    let _self = this;

    this.cashPositioningNew.push({
      series: series,
      labels: processedData.labels,
      colors: seriesColors,
      chart: {
        type: 'bar',
        height: 400,
        stacked: false,
        zoom: {
          enabled: true,
          type: 'x',
          autoScaleYaxis: true
        },
        toolbar: {
          show: true,
          tools: {
            download: true,
            selection: true,
            zoom: true,
            zoomin: true,
            zoomout: true,
            pan: true,
            reset: true
          },
          export: {
            csv: {
              filename: `Cash_Performance_${this.selectedAggregation}_${new Date().toISOString().split('T')[0]}`,
            }
          }
        },
        selection: {
          enabled: true,
          type: 'x'
        },
        animations: {
          enabled: true,
          easing: 'easeinout',
          speed: 800
        }
      },

      yaxis: {
        labels: {
          formatter: function (value: any) {
            return _self.showAsInteger ?
              _self._decimalPipe.transform(value, '1.0-0') + ' ' + Unit :
              _self._decimalPipe.transform(value, '1.2-2') + ' ' + Unit;
          }
        },
        title: {
          text: `Amount (${Unit})`
        }
      },

      xaxis: {
        type: 'category',
        categories: processedData.labels,
        title: {
          text: `Date (${this.selectedQuarter === 'ALL' ? 'Full Year' : this.selectedQuarter} - ${this.selectedAggregation})`
        },
        labels: {
          rotate: -45,
          style: {
            fontSize: '11px',
            colors: '#666'
          }
        }
      },

      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '65%',
          dataLabels: {
            position: 'top',
          }
        },
      },

      stroke: {
        width: 1,
        colors: ['#fff']
      },

      fill: {
        opacity: 0.9
      },

      tooltip: {
        enabled: true,
        shared: false,
        intersect: true,
        custom: this.getTooltipFormatter(this.selectedAggregation, Unit)
      },

      grid: {
        show: true,
        borderColor: '#e9ecef',
        strokeDashArray: 2
      },

      dataLabels: {
        enabled: false,
        formatter: function (val: any) {
          const formattedValue = _self.showAsInteger ?
            _self._decimalPipe.transform(val, '1.0-0') :
            _self._decimalPipe.transform(val, '1.2-2');
          return `${formattedValue} ${Unit}`;
        }
      },

      legend: {
        show: true,
        position: 'top',
        horizontalAlign: 'center'
      }
    });
  }

  //  ADD: Enhanced tooltip formatter
  private getTooltipFormatter(aggregationType: string, unit: string) {
    let _self = this;
    return function ({ series, seriesIndex, dataPointIndex, w }: any) {
      const categoryName = w.globals.labels[dataPointIndex];
      const value = series[seriesIndex][dataPointIndex];

      let dateInfo = '';
      let valueLabel = '';

      switch (aggregationType) {
        case 'daily':
          const date = new Date(categoryName);
          dateInfo = date.toLocaleDateString('en-US', {
            weekday: 'short',
            year: 'numeric',
            month: 'short',
            day: 'numeric'
          });
          valueLabel = 'Daily Balance';
          break;

        case 'weekly':
          dateInfo = `Week of ${categoryName}`;
          valueLabel = 'Weekly Average';
          break;

        case 'monthly':
          dateInfo = categoryName;
          valueLabel = 'Monthly Average';
          break;
      }

      const formattedValue = _self.showAsInteger ?
        _self._decimalPipe.transform(value, '1.0-0') :
        _self._decimalPipe.transform(value, '1.2-2');

      return `
        <div class="custom-tooltip" style="
          background: white; 
          padding: 15px; 
          border-radius: 8px; 
          box-shadow: 0 4px 20px rgba(0,0,0,0.15);
          border: 1px solid #e9ecef;
          min-width: 200px;
        ">
          <div style="font-weight: 600; margin-bottom: 10px; color: #495057;">
            ${dateInfo}
          </div>
          <div style="display: flex; align-items: center;">
            <div style="width: 12px; height: 12px; background: #749819; border-radius: 3px; margin-right: 10px;"></div>
            <span style="font-weight: 500;">${valueLabel}:</span>
            <span style="font-weight: 600; color: #749819; margin-left: 8px;">
              ${formattedValue} ${unit}
            </span>
          </div>
        </div>
      `;
    };
  }

  //  ADD: Quarter filter change handler
  onQuarterFilterChange(chartIndex: number, quarter: string): void {
    this.selectedQuarter = quarter;

    if (this.originalChartData[chartIndex]) {
      const processedData = this.smartDataProcessing(
        this.originalChartData[chartIndex].labels,
        this.originalChartData[chartIndex].data,
        quarter
      );

      this.updateChartData(chartIndex, processedData);
    }
  }

  //  ADD: Aggregation change handler
  onAggregationChange(chartIndex: number, aggregationType: string): void {
    this.selectedAggregation = aggregationType;

    if (this.originalChartData[chartIndex]) {
      // First apply quarter filter
      const quarterFiltered = this.filterDataByQuarter(
        this.originalChartData[chartIndex].labels,
        this.originalChartData[chartIndex].data,
        this.selectedQuarter
      );

      // Then apply aggregation
      const aggregatedData = this.aggregateData(
        quarterFiltered.labels,
        quarterFiltered.data,
        aggregationType
      );

      this.updateChartData(chartIndex, aggregatedData);
    }
  }


  //  ADD: Update chart data method
  private updateChartData(chartIndex: number, newData: { labels: string[], data: number[] }) {
    this.cashPositioningNew[chartIndex].series = [{
      name: 'Cash Balance',
      data: newData.data
    }];
    this.cashPositioningNew[chartIndex].labels = newData.labels;
    this.cashPositioningNew[chartIndex].xaxis.categories = newData.labels;
    this.cashPositioningNew[chartIndex].xaxis.title.text =
      `Date (${this.selectedQuarter === 'ALL' ? 'Full Year' : this.selectedQuarter} - ${this.selectedAggregation})`;

    // Update tooltip
    this.cashPositioningNew[chartIndex].tooltip.custom = this.getTooltipFormatter(
      this.selectedAggregation,
      this.originalChartData[chartIndex].unit
    );

    const chartComponent = this.charts.toArray()[chartIndex];
    if (chartComponent) {
      chartComponent.updateSeries([{
        name: 'Cash Balance',
        data: newData.data
      }]);

      chartComponent.updateOptions({
        xaxis: {
          categories: newData.labels,
          title: {
            text: `Date (${this.selectedQuarter === 'ALL' ? 'Full Year' : this.selectedQuarter} - ${this.selectedAggregation})`
          }
        },
        tooltip: {
          custom: this.getTooltipFormatter(this.selectedAggregation, this.originalChartData[chartIndex].unit)
        }
      });
    }
  }


  //  ADD: Helper methods for template
  getCurrentQuarter(chartIndex: number): string {
    return this.selectedQuarter;
  }

  getCurrentAggregation(): string {
    return this.selectedAggregation;
  }

  getQuarterMonths(quarter: string): string {
    const quarterMap: { [key: string]: string } = {
      'Q1': 'Jan-Mar',
      'Q2': 'Apr-Jun',
      'Q3': 'Jul-Sep',
      'Q4': 'Oct-Dec'
    };
    return quarterMap[quarter] || '';
  }

  getAggregationDescription(aggregationType: string): string {
    const descriptions: { [key: string]: string } = {
      'daily': 'Show every day',
      'weekly': 'Average per week',
      'monthly': 'Average per month'
    };
    return descriptions[aggregationType] || '';
  }

  //  Enhanced toggle methods for cash performance charts
  toggleCashDataLabels(chartIndex: number): void {
    if (this.cashPositioningNew[chartIndex]) {
      this.cashPositioningNew[chartIndex].dataLabels.enabled = !this.cashPositioningNew[chartIndex].dataLabels.enabled;

      const chartComponent = this.charts.toArray()[chartIndex];
      if (chartComponent) {
        chartComponent.updateOptions({
          dataLabels: this.cashPositioningNew[chartIndex].dataLabels
        }, false, true);
      }
    }
  }

  changeCashChartType(chartIndex: number, newType: string): void {
    if (this.cashPositioningNew[chartIndex]) {
      this.cashPositioningNew[chartIndex].chart.type = newType;

      // Adjust settings based on chart type
      if (newType === 'area') {
        this.cashPositioningNew[chartIndex].stroke = {
          width: 2,
          curve: 'smooth'
        };
        this.cashPositioningNew[chartIndex].markers = {
          size: 4,
          hover: {
            size: 7
          }
        };
        this.cashPositioningNew[chartIndex].fill = {
          // type: 'gradient',
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.7,
            opacityTo: 0.9,
            stops: [0, 90, 100]
          }
        };

        // Adjust data labels for area charts
        if (this.cashPositioningNew[chartIndex].dataLabels.enabled) {
          this.cashPositioningNew[chartIndex].dataLabels.offsetY = -15;
          this.cashPositioningNew[chartIndex].dataLabels.background.padding = 6;
          this.cashPositioningNew[chartIndex].dataLabels.background.opacity = 0.98;
        }

      } else {
        // Bar chart settings
        this.cashPositioningNew[chartIndex].stroke = {
          width: 1,
          colors: ['#fff']
        };
        delete this.cashPositioningNew[chartIndex].markers;

        this.cashPositioningNew[chartIndex].fill = {
          opacity: 0.9,
          // type: 'gradient',
          gradient: {
            shade: 'light',
            type: 'vertical',
            shadeIntensity: 0.1,
            gradientToColors: undefined,
            inverseColors: false,
            opacityFrom: 0.9,
            opacityTo: 0.75,
            stops: [0, 90, 100]
          }
        };

        // Reset data labels for bar charts
        if (this.cashPositioningNew[chartIndex].dataLabels.enabled) {
          this.cashPositioningNew[chartIndex].dataLabels.offsetY = -8;
          this.cashPositioningNew[chartIndex].dataLabels.background.padding = 4;
          this.cashPositioningNew[chartIndex].dataLabels.background.opacity = 0.95;
        }
      }

      const chartComponent = this.charts.toArray()[chartIndex];
      if (chartComponent) {
        chartComponent.updateOptions({
          chart: { type: newType },
          stroke: this.cashPositioningNew[chartIndex].stroke,
          fill: this.cashPositioningNew[chartIndex].fill,
          markers: this.cashPositioningNew[chartIndex].markers,
          dataLabels: this.cashPositioningNew[chartIndex].dataLabels
        }, false, true);
      }
    }
  }

  // Helper methods for cash performance charts
  isCashDataLabelsEnabled(chartIndex: number): boolean {
    return this.cashPositioningNew[chartIndex]?.dataLabels?.enabled || false;
  }

  getCurrentCashChartType(chartIndex: number): string {
    return this.cashPositioningNew[chartIndex]?.chart?.type || 'bar';
  }



  RGBAToHexA(rgba: any, forceRemoveAlpha = false) {
    return "#" + rgba.replace(/^rgba?\(|\s+|\)$/g, '') // Get's rgba / rgb string values
      .split(',') // splits them at ","
      .filter((string: string, index: number) => !forceRemoveAlpha || index !== 3)
      .map((string: string) => parseFloat(string)) // Converts them to numbers
      .map((number: number, index: number) => index === 3 ? Math.round(number * 255) : number) // Converts alpha to 255 number
      .map((number: number) => number.toString(16)) // Converts numbers to hex
      .map((string: string) => string.length === 1 ? "0" + string : string) // Adds 0 when length of one number is 1
      .join("") // Puts the array to togehter to a string
  }
}
